import { Logger } from '../../common'
import { DBConnection, ROTransaction, RWTransaction } from '../db'
import { IDB_MIGRATIONS } from './idbMigrations'
import { IDBPDatabase, IDBPTransaction, openIndexedDB } from './promised.idb'

export class IndexedDBConnection implements DBConnection {
  private logger = new Logger('IndexedDBConnection')

  private db: IDBPDatabase | undefined

  constructor(private readonly dbName: string) {}

  name = 'indexedDb'

  async close(): Promise<void> {
    this.db?.close()
  }

  async init(): Promise<IDBPDatabase> {
    if (!this.db) {
      const migrations = IDB_MIGRATIONS.map(clazz => new clazz())
      const latestDbVersion = migrations.map(m => m.version).reduce((prev, cur) => Math.max(prev, cur))
      this.db = await openIndexedDB(this.dbName, latestDbVersion, {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onUpgrade(db, oldVersion, newVersion) {
          migrations.forEach(m => {
            if (m.version > oldVersion) m.up(db)
          })
        },
      })
    }
    return this.db
  }

  async startROTransaction(): Promise<ROTransaction> {
    const db = await this.init()
    const tr = db.transaction(db.objectStoreNames)
    return new IndexedDBROTransaction(tr)
  }

  async startRWTransaction(): Promise<RWTransaction> {
    const db = await this.init()
    const tr = db.transaction(db.objectStoreNames, 'readwrite')
    return new IndexedDBRWTransaction(tr)
  }
}

export class IndexedDBROTransaction implements ROTransaction {
  constructor(readonly idbTransaction: IDBPTransaction) {}

  close(): Promise<void> {
    return Promise.resolve()
  }

  static txFrom(tx: ROTransaction): IDBPTransaction {
    if (tx instanceof IndexedDBROTransaction) {
      return (tx as IndexedDBROTransaction).idbTransaction
    } else if (tx instanceof IndexedDBRWTransaction) {
      return (tx as IndexedDBRWTransaction).idbTransaction
    } else {
      throw new Error('<7e05672f> Wrong transaction type')
    }
  }
}

export class IndexedDBRWTransaction extends IndexedDBROTransaction implements RWTransaction {
  private finished = false
  private static logger = new Logger('IndexedDBRWTransaction')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (reason: any) => void

  constructor(idbTransaction: IDBPTransaction) {
    super(idbTransaction)
    idbTransaction.onError = reason => {
      IndexedDBRWTransaction.logger.error({}, `<65dbc4d8>`, reason)
      this.finished = true
      this.onError && this.onError(reason)
    }
  }

  async commit(): Promise<void> {
    if (this.finished) return
    this.finished = true
    this.idbTransaction.commit()
  }

  async rollback(): Promise<void> {
    if (this.finished) return
    this.finished = true
    IndexedDBRWTransaction.logger.error({}, '<1a6d1099> Rollback')
    this.idbTransaction.abort()
  }
}
